import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { IoIosNotificationsOutline } from "react-icons/io";
import { format } from "date-fns";
import ApiClient from "../../methods/api/apiClient";
import environment from "../../environment";

const SOCKET_URL = environment.api || "http://localhost:5000";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return format(new Date(dateStr), "MMM dd, yyyy");
}

function getRedirectPath(notif) {
  const meta = notif?.meta;
  if (!notif?.sourceId) return null;

  const id = notif.sourceId;
  const date = Date.now();

  const typeToPath = {
    "generate-quote": `/estimate/detail/${id}?date=${date}`,
    "estimated-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "reject-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "invoice-created": `/invoice/detail/${id}?date=${date}`,
    "shipment-update": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-date-update": `/order/tracking/detail/${id}?date=${date}`,
    "tracking-url": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-with-due": `/order/tracking/detail/${id}?date=${date}`,
    "order-delivered": `/order/tracking/detail/${id}?date=${date}`,
    "payment-completed": `/invoice/detail/${id}?date=${date}`,
  };

  return typeToPath[meta.type] ?? null;
}

function NotificationModal({ notif, onClose, onMarkRead }) {
  if (!notif) return null;

  const handleModalClick = (e) => e.stopPropagation();

  const handleMarkRead = () => {
    if (!notif.isRead) onMarkRead(notif._id);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]"
        onClick={handleModalClick}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
              <svg
                stroke="currentColor"
                fill="currentColor"
                stroke-width="0"
                viewBox="0 0 512 512"
                className="text-[#115B9D]"
                height="18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M289.7 403c-6.1 0-11.4 4.2-12.7 10.2-1 4.5-2.7 8.2-5 10.9-1.3 1.5-5.1 5.9-16.1 5.9-11 0-14.8-4.5-16.1-5.9-2.3-2.7-4-6.4-5-10.9-1.3-6-6.6-10.2-12.7-10.2-8.4 0-14.5 7.8-12.7 15.9 5 22.3 21 37.1 46.5 37.1s41.5-14.7 46.5-37.1c1.8-8.1-4.4-15.9-12.7-15.9zM412 352.2c-15.4-20.3-45.7-32.2-45.7-123.1 0-93.3-41.2-130.8-79.6-139.8-3.6-.9-6.2-2.1-6.2-5.9v-2.9c0-13.3-10.8-24.6-24-24.6h-.6c-13.2 0-24 11.3-24 24.6v2.9c0 3.7-2.6 5-6.2 5.9-38.5 9.1-79.6 46.5-79.6 139.8 0 90.9-30.3 102.7-45.7 123.1-9.9 13.1-.5 31.8 15.9 31.8h280.1c16.1 0 25.4-18.8 15.6-31.8zm-39 5.8H139.8c-3.8 0-5.8-4.4-3.3-7.3 7-8 14.7-18.5 21-33.4 9.6-22.6 14.3-51.5 14.3-88.2 0-37.3 7-66.5 20.9-86.8 12.4-18.2 27.9-25.1 38.7-27.6 8.4-2 14.4-5.8 18.6-10.5 3.2-3.6 8.7-3.8 11.9-.2 5.1 5.7 12 9.1 18.8 10.7 10.8 2.5 26.3 9.4 38.7 27.6 13.9 20.3 20.9 49.5 20.9 86.8 0 36.7 4.7 65.6 14.3 88.2 6.5 15.2 14.4 25.9 21.5 33.9 2.2 2.7.4 6.8-3.1 6.8z"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Notification Details
              </h3>
              {!notif.isRead && (
                <div className="">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    <span className="size-1.5 rounded-full bg-amber-500" />
                    Unread
                  </span>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">
          <div className="">
            <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">
              Title
            </label>
            <p className="text-sm text-gray-800 mt-0.5 break-words">
              {notif.title || "Notification"}
            </p>
          </div>

          <div className="">
            <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">
              Message
            </label>
            <p className="text-sm text-gray-800 mt-0.5 break-words">
              {notif.message}
            </p>
          </div>

          <div className="">
            <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">
              Sent By
            </label>
            <div className="flex items-center gap-2">
              {notif.sendBy?.image && (
                <img
                  src={notif.sendBy.image}
                  alt={notif.sendBy.fullName}
                  className="w-6 h-6 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-gray-900 font-medium">
                  {notif.sendBy?.fullName || "System"}
                </p>
                {notif.sendBy?.email && (
                  <p className="text-xs text-gray-500">{notif.sendBy.email}</p>
                )}
              </div>
            </div>
          </div>

          <div className="">
            <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">
              Received At
            </label>
            <p className="text-sm text-gray-800 mt-0.5 break-words">
              {format(new Date(notif.createdAt), "PPP 'at' h:mm a")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={handleMarkRead}
            className={
              !notif.isRead
                ? "w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                : "inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
            }
          >
            {!notif.isRead ? "Mark as Read & Close" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationRow({ notif, onRead, onOpenModal, onNavigate }) {
  const handleClick = () => {
    if (!notif?.isRead) onRead(notif?._id);

    const path = getRedirectPath(notif);
    if (path) {
      onNavigate(path);
    } else {
      onNavigate("/orders");
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors ${
        notif.isRead ? "hover:bg-gray-50" : "bg-blue-50/40 hover:bg-blue-50/60"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          notif.isRead
            ? "bg-gray-100 text-gray-600"
            : "bg-blue-100 text-blue-600"
        }`}
      >
        {notif.sendBy?.fullName?.[0]?.toUpperCase() || "N"}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">
            {notif.title || "Notification"}
          </p>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {timeAgo(notif.createdAt)}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-0.5">{notif.message}</p>
        <p className="text-xs text-gray-400 mt-1 capitalize">
          From:{" "}
          {notif.sendBy?.fullName || notif.senderDetails?.fullName || "N/A"}
        </p>
      </div>

      {!notif.isRead && (
        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
      )}
    </div>
  );
}

export default function NotificationBell({ colour }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const userId = user?._id || user?.id;

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const socketRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await ApiClient.get(
        `notifications/list?isRead=false&page=1&limit=200000`,
      );
      if (res?.success) {
        const newData = res.data || [];
        setNotifications(newData);
        setTotalUnread(newData.filter((n) => !n.isRead).length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markReadAPI = async (id) => {
    try {
      await ApiClient.put(`notifications/update`, { isRead: true, id });
      // setTotalUnread((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllReadAPI = async () => {
    try {
      await ApiClient.post(`notifications/mark-all-read`, { userId });
      setNotifications([]);
      setTotalUnread(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  useEffect(() => {
    if (!userId) return;

    if (!initialFetchDone) {
      fetchNotifications();
      setInitialFetchDone(true);
    }

    const socket = io(`${SOCKET_URL}?userId=${userId}`, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on("connect", () =>
      console.log("Socket connected for notifications"),
    );

    socket.on("notification", (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      if (!newNotification.isRead) setTotalUnread((prev) => prev + 1);
    });

    socket.on("connect_error", (error) =>
      console.error("Socket connection error:", error),
    );

    return () => {
      if (socket) socket.disconnect();
    };
  }, [userId, initialFetchDone, fetchNotifications]);

  const handleMarkRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n?._id === id ? { ...n, isRead: true } : n)),
    );
    setTotalUnread((prev) => Math.max(0, prev - 1));
    await markReadAPI(id);
  }, []);

  const handleMarkAllRead = async () => {
    await markAllReadAPI();
  };

  const handleOpenModal = (notification) => {
    setSelectedNotification(notification);
    if (!notification.isRead) handleMarkRead(notification._id);
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
    setOpen(false);
  };

  // ─── Navigate and close dropdown ──────────────────────────────────────────
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleToggleDropdown = () => {
    setOpen((prev) => !prev);
    if (!open) fetchNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown}
        className={`relative p-2 ${colour} hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none`}
      >
        <IoIosNotificationsOutline size={22} />
        {totalUnread > 0 && (
          <span className="absolute top-1 right-2.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[18px] h-[18px]">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 pb-5">
          <div className="px-4 py-3 border-b bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <IoIosNotificationsOutline
                    size={24}
                    className="text-gray-400"
                  />
                </div>
                <p className="text-gray-500 text-sm">No unread notifications</p>
                <p className="text-gray-400 text-xs mt-1">
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <NotificationRow
                    key={notif._id}
                    notif={notif}
                    onRead={handleMarkRead}
                    onOpenModal={handleOpenModal}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedNotification && (
        <NotificationModal
          notif={selectedNotification}
          onClose={handleCloseModal}
          onMarkRead={handleMarkRead}
        />
      )}
    </div>
  );
}
